import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectGameTags } from '../../store/selectors/gamesSelectors';
import { fetchGameTags } from '../../store/thunks/gamesThunks';

import './GameTagInput.css';

type GameTagInputProps = {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
};

export function GameTagInput({ selectedTags, onTagsChange }: GameTagInputProps) {
  const dispatch = useAppDispatch();
  const allTags = useAppSelector(selectGameTags);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allTagsExpanded, setAllTagsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (allTags.length === 0) {
      dispatch(fetchGameTags());
    }
  }, [dispatch, allTags.length]);

  const allTagNames = allTags.map((t) => t.name);

  const availableTags = allTagNames.filter((name) => !selectedTags.includes(name));

  const suggestions = availableTags.filter((name) =>
    name.toLowerCase().includes(inputValue.toLowerCase().trim()),
  );

  const addTag = useCallback(
    (tag: string) => {
      const normalized = tag.toLowerCase().trim();
      if (normalized && !selectedTags.includes(normalized)) {
        onTagsChange([...selectedTags, normalized]);
      }
      setInputValue('');
      setShowSuggestions(false);
    },
    [selectedTags, onTagsChange],
  );

  const removeTag = useCallback(
    (tag: string) => {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    },
    [selectedTags, onTagsChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitInput();
    }
  };

  const submitInput = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="game-tag-input">
      {selectedTags.length > 0 && (
        <div className="game-tag-input__selected">
          {selectedTags.map((tag) => (
            <span key={tag} className="game-tag-input__chip">
              {tag}
              <button
                className="game-tag-input__chip-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Удалить тег ${tag}`}
                type="button"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="game-tag-input__field-wrap">
        <input
          ref={inputRef}
          className="game-tag-input__field"
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder="Введите тег..."
        />
        <button
          className="game-tag-input__submit-btn"
          onClick={submitInput}
          disabled={!inputValue.trim()}
          aria-label="Добавить тег"
          type="button"
        >
          ↵
        </button>

        {showSuggestions && inputValue.trim() && suggestions.length > 0 && (
          <div className="game-tag-input__suggestions">
            {suggestions.slice(0, 8).map((name) => (
              <button
                key={name}
                className="game-tag-input__suggestion"
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(name);
                }}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {availableTags.length > 0 && (
        <div className="game-tag-input__all-tags">
          <button
            className="game-tag-input__toggle-btn"
            onClick={() => setAllTagsExpanded((prev) => !prev)}
            type="button"
          >
            <span className={`game-tag-input__toggle-icon${allTagsExpanded ? ' game-tag-input__toggle-icon--open' : ''}`}>
              ▸
            </span>
            Все теги ({availableTags.length})
          </button>

          {allTagsExpanded && (
            <div className="game-tag-input__all-tags-list">
              {availableTags.map((name) => (
                <button
                  key={name}
                  className="game-tag-input__pickable-tag"
                  onClick={() => addTag(name)}
                  type="button"
                >
                  + {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
